import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import { Layout } from './components/layout/Layout';

import Dashboard from './pages/Dashboard';
import CustomersPage from './pages/CustomersPage';
import CustomerDetailPage from './pages/CustomerDetailPage';
import LeadsPage from './pages/LeadsPage';
import LeadsDashboardPage from './pages/LeadsDashboardPage';
import LeadDetailPage from './pages/LeadDetailPage';
import ProductsPage from './pages/ProductsPage';
import PriceListsPage from './pages/PriceListsPage';
import CalendarPage from './pages/CalendarPage';
import ActivitiesPage from './pages/ActivitiesPage';

import InvoicesPage from './pages/InvoicesPage';
import PassiveInvoicesPage from './pages/PassiveInvoicesPage';
import SubscriptionsPage from './pages/SubscriptionsPage';
import AnalisiAziendaliPage from './pages/AnalisiAziendaliPage';
import CommissionsPage from './pages/CommissionsPage';
import EmailContactsPage from './pages/EmailContactsPage';
import EmailListePage from './pages/EmailListePage';
import EmailCampaignsPage from './pages/EmailCampaignsPage';
import EmailCampaignEditorPage from './pages/EmailCampaignEditorPage';
import EmailAutomationsPage from './pages/EmailAutomationsPage';
import EmailAutomationEditorPage from './pages/EmailAutomationEditorPage';
import EmailTemplatesPage from './pages/EmailTemplatesPage';
import NotificationsPage from './pages/NotificationsPage';
import UserManagementPage from './pages/UserManagementPage';
import { DocumentConfigurationPage } from './pages/DocumentConfigurationPage';
import SettingsPage from './pages/SettingsPage';
import LoginPage from './pages/auth/LoginPage';
import SignupPage from './pages/auth/SignupPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import NewsletterPage from './pages/NewsletterPage';
import IscrizioneCorsiPage from './pages/IscrizioneCorsiPage';
import RegistrazioneEventoPage from './pages/RegistrazioneEventoPage';
import EventiPage from './pages/EventiPage';
import EventiDettaglioPage from './pages/EventiDettaglioPage';

import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { MailingLayout } from './components/email/MailingLayout';

import Toast from './components/Toast';
import { LoadingFallback } from './components/LoadingFallback';
import { ErrorBoundary } from './components/ErrorBoundary';
import { UserRole } from './types/roles';
import { useVersionCheck } from './hooks/useVersionCheck';
import { useSettingsStore } from './store/useSettingsStore';
import { ReloadPrompt } from './components/ReloadPrompt';

/**
 * La sezione si chiamava Email e adesso si chiama Mailing. Chi ha un link
 * vecchio da qualche parte -- un segnalibro, una email, un messaggio -- viene
 * portato alla pagina corrispondente invece che su una pagina bianca.
 */
function VecchioIndirizzoEmail() {
  const posizione = useLocation();
  const nuovo = posizione.pathname.replace(/^\/email/, '/mailing');
  return <Navigate to={`${nuovo}${posizione.search}`} replace />;
}

/**
 * Pagine aperte a chiunque, senza login. Non aspettano di sapere se c'e' un
 * utente collegato: chi arriva da un link esterno vede subito la pagina,
 * invece di "Caricamento applicazione...".
 */
const PERCORSI_PUBBLICI = ['/newsletter/', '/iscrizione-corsi'];

function App() {
  const { user, profile, loading, error } = useAuth();
  const [bootBypass, setBootBypass] = useState(false);

  // Controlla periodicamente se c'è una nuova versione disponibile
  useVersionCheck();

  // Le impostazioni dell'azienda si leggono una volta sola, appena si sa chi e'
  // collegato. Servono prima di qualunque PDF o email: e' da li' che arrivano
  // ragione sociale, P.IVA e numero WhatsApp.
  const caricaImpostazioni = useSettingsStore((s) => s.carica);
  useEffect(() => {
    if (user) void caricaImpostazioni();
  }, [user, caricaImpostazioni]);

  const renderWithRoles = (roles: UserRole[], element: JSX.Element) => (
    <ProtectedRoute requiredRole={roles}>{element}</ProtectedRoute>
  );

  // If loading persists unusually long, bypass as logged-out to avoid infinite spinner
  useEffect(() => {
    if (!loading) return;
    const t = setTimeout(() => setBootBypass(true), 1500);
    return () => clearTimeout(t);
  }, [loading]);

  // Remove verbose console logs for production readiness

  // Show loading only for a brief moment, then show login if no user
  const paginaPubblica = PERCORSI_PUBBLICI.some((percorso) => window.location.pathname.startsWith(percorso));
  if (loading && !bootBypass && !paginaPubblica) {
    return <LoadingFallback message="Caricamento applicazione..." />;
  }

  // If there's an auth error, always show login page
  if (error) {
    // Optionally, show a toast via global store; avoid console noise
  }

  return (
    <ErrorBoundary>
      <BrowserRouter>
        <Routes>
          {/* Always make auth routes available */}
          <Route path="/auth/login" element={<LoginPage />} />
          <Route path="/auth/signup" element={<SignupPage />} />
          <Route path="/auth/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
          {/* Chi riceve le nostre email arriva qui dai link in fondo al messaggio: niente login. */}
          <Route path="/newsletter/:azione" element={<NewsletterPage />} />
          {/* Modulo di iscrizione ai corsi: lo compila chiunque, da un link o dal sito. */}
          <Route path="/iscrizione-corsi" element={<IscrizioneCorsiPage />} />
          {/* Registrazione dei biglietti comprati sul sito: si apre dal link personale dell'email. */}
          <Route path="/registrazione-evento" element={<RegistrazioneEventoPage />} />

          {!user ? (
            // Redirect all other routes to login when not authenticated
            <>
              <Route path="*" element={<Navigate to="/auth/login" replace />} />
            </>
          ) : (
            // Protected routes when authenticated (non-production users)
            <>

              {/* Main app routes */}
              <Route path="/" element={<Layout />}>
                <Route
                  index
                  element={renderWithRoles(['admin', 'sales', 'commerciale', 'amministrazione', 'lettore'], <Dashboard />)}
                />
                <Route path="customers" element={renderWithRoles(['admin', 'sales', 'commerciale', 'amministrazione'], <CustomersPage />)} />
                <Route path="customers/:id" element={renderWithRoles(['admin', 'sales', 'commerciale', 'amministrazione'], <CustomerDetailPage />)} />
                <Route path="leads" element={renderWithRoles(['admin', 'sales', 'commerciale', 'amministrazione'], <LeadsPage />)} />
                <Route path="leads/stats" element={renderWithRoles(['admin', 'sales', 'commerciale', 'amministrazione'], <LeadsDashboardPage />)} />
                <Route path="leads/:id" element={renderWithRoles(['admin', 'sales', 'commerciale', 'amministrazione'], <LeadDetailPage />)} />
                <Route path="products" element={renderWithRoles(['admin', 'sales', 'commerciale', 'amministrazione', 'lettore'], <ProductsPage />)} />
                <Route path="price-lists" element={renderWithRoles(['admin', 'sales', 'commerciale', 'amministrazione'], <PriceListsPage />)} />
                <Route path="invoices" element={renderWithRoles(['admin', 'commerciale', 'amministrazione'], <InvoicesPage />)} />
                <Route path="passive-invoices" element={renderWithRoles(['admin', 'commerciale', 'amministrazione'], <PassiveInvoicesPage />)} />
                <Route path="subscriptions" element={renderWithRoles(['admin', 'commerciale', 'amministrazione', 'sales'], <SubscriptionsPage />)} />
                <Route path="analisi-aziendali" element={renderWithRoles(['admin', 'commerciale', 'amministrazione'], <AnalisiAziendaliPage />)} />
                <Route path="activities" element={renderWithRoles(['admin', 'sales', 'commerciale', 'amministrazione'], <ActivitiesPage />)} />
                <Route path="commissions" element={renderWithRoles(['admin', 'commerciale', 'amministrazione'], <CommissionsPage />)} />
                <Route path="calendar" element={renderWithRoles(['admin', 'sales', 'commerciale', 'amministrazione'], <CalendarPage />)} />

                {/* Mailing. La voce di menu punta a /mailing: da li' si entra
                    nella prima pagina della sezione. Le quattro schede stanno
                    dentro MailingLayout, che disegna titolo e linguette una
                    volta sola; gli editor ne restano fuori. */}
                <Route path="mailing" element={<MailingLayout />}>
                  <Route index element={<Navigate to="/mailing/campagne" replace />} />
                  <Route path="campagne" element={renderWithRoles(['admin', 'commerciale'], <EmailCampaignsPage />)} />
                  <Route path="contatti" element={renderWithRoles(['admin', 'sales', 'commerciale', 'amministrazione'], <EmailContactsPage />)} />
                  <Route path="liste" element={renderWithRoles(['admin', 'sales', 'commerciale', 'amministrazione'], <EmailListePage />)} />
                  <Route path="modelli" element={renderWithRoles(['admin', 'sales', 'commerciale'], <EmailTemplatesPage />)} />
                  <Route path="sequenze" element={renderWithRoles(['admin', 'commerciale'], <EmailAutomationsPage />)} />
                </Route>
                <Route path="mailing/campagne/:id" element={renderWithRoles(['admin', 'commerciale'], <EmailCampaignEditorPage />)} />
                <Route path="mailing/sequenze/:id" element={renderWithRoles(['admin', 'commerciale'], <EmailAutomationEditorPage />)} />

                {/* Eventi (congressi, convegni): elenco e pagina dell'evento con
                    le schede Impostazioni, Richieste, Assegnazioni. */}
                <Route path="eventi" element={renderWithRoles(['admin', 'commerciale'], <EventiPage />)} />
                <Route path="eventi/:id" element={renderWithRoles(['admin', 'commerciale'], <EventiDettaglioPage />)} />

                {/* I vecchi indirizzi restano buoni: chi si e' salvato un link
                    a /email lo ritrova, non gli si apre una pagina bianca. */}
                <Route path="email/*" element={<VecchioIndirizzoEmail />} />
                <Route path="email" element={<Navigate to="/mailing/campagne" replace />} />

                <Route path="notifications" element={renderWithRoles(['admin', 'sales', 'commerciale', 'lettore'], <NotificationsPage />)} />
                <Route
                  path="user-management"
                  element={
                    <ProtectedRoute requiredRole="admin">
                      <UserManagementPage />
                    </ProtectedRoute>
                  }
                />
                {/* Aperta a tutti: ognuno ci cambia la propria password. Le parti
                    sull'azienda la pagina le mostra solo agli amministratori. */}
                <Route path="impostazioni" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
                <Route
                  path="document-configuration"
                  element={renderWithRoles(['admin', 'commerciale', 'amministrazione'], <DocumentConfigurationPage />)}
                />
              </Route>

              {/* Redirect auth routes to dashboard when already logged in */}
              <Route path="/auth/*" element={<Navigate to="/" replace />} />

              {/* Catch-all route for authenticated users */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </>
          )}
        </Routes>
      </BrowserRouter>
      <Toast />
      <ReloadPrompt />
    </ErrorBoundary>
  );
}

export default App;